/**
 * SearchOverlay — globales Suchfenster über Produkte, Kategorien und Seiten.
 *
 * Öffnet sich als Modal aus dem Header (Lupen-Icon). Live-Suche mit
 * 250ms Debounce, gruppierte Ergebnisse in drei Sektionen.
 *
 * UX-Entscheidungen:
 * - Autofokus im Input beim Öffnen — Nutzer kann sofort tippen
 * - Escape-Taste schließt
 * - Klick auf Ergebnis navigiert und schließt Modal
 * - Bei leerem Query: freundlicher Hinweis + Trending-Kategorien (optional TODO)
 * - Ab 2 Zeichen wird gesucht
 * - Ladeindikator während Request
 * - "Keine Treffer"-State mit Vorschlag
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, Loader2, Package, LayoutGrid, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);
  const abortRef = React.useRef(null);
  const navigate = useNavigate();

  // Autofokus + Body-Scroll-Lock beim Öffnen
  React.useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Escape schließt
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset bei Schließen
  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(null);
      setError(null);
    }
  }, [open]);

  // Debounced Suche
  React.useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    // Vorherigen Request abbrechen wenn User weiter tippt
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/shop/search`, {
          params: { q: trimmed, limit: 8 },
          signal: ac.signal,
        });
        setResults(res.data);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
          setError('Suche fehlgeschlagen. Bitte erneut versuchen.');
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [query, open]);

  const go = (path) => {
    onClose();
    setTimeout(() => navigate(path), 30);
  };

  if (!open) return null;

  const trimmed = query.trim();
  const hasResults = results && (results.products?.length || results.categories?.length || results.pages?.length);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:p-8 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="search-overlay-backdrop"
    >
      <div
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl mt-8 sm:mt-16 overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="search-overlay"
      >
        {/* Input-Zeile */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-200">
          <Search className="w-5 h-5 text-stone-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Artikel, Kategorie oder Angebot..."
            className="flex-1 text-base sm:text-lg outline-none placeholder:text-stone-400 bg-transparent"
            data-testid="search-overlay-input"
          />
          {loading && <Loader2 className="w-4 h-4 text-stone-400 animate-spin" />}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-stone-100 text-stone-500"
            aria-label="Schließen"
            data-testid="search-overlay-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Ergebnis-Bereich */}
        <div className="flex-1 overflow-y-auto">
          {/* Leer-State */}
          {trimmed.length < MIN_QUERY && (
            <div className="px-6 py-12 text-center text-sm text-stone-500">
              <Search className="w-8 h-8 mx-auto mb-3 text-stone-300" />
              <p>Gib mindestens 2 Zeichen ein, um zu suchen.</p>
              <p className="mt-1 text-xs text-stone-400">Wir durchsuchen Produkte, Kategorien und Angebote.</p>
            </div>
          )}

          {error && (
            <div className="px-6 py-8 text-center text-sm text-red-600" data-testid="search-overlay-error">
              {error}
            </div>
          )}

          {trimmed.length >= MIN_QUERY && !loading && results && !hasResults && (
            <div className="px-6 py-12 text-center text-sm text-stone-500" data-testid="search-overlay-empty">
              <p>Keine Treffer für <span className="font-medium text-stone-700">„{trimmed}"</span></p>
              <p className="mt-2 text-xs text-stone-400">Tipp: kürzere Begriffe oder verwandte Wörter probieren.</p>
            </div>
          )}

          {hasResults && (
            <div className="divide-y divide-stone-100" data-testid="search-overlay-results">
              {/* Produkte */}
              {results.products?.length > 0 && (
                <SearchGroup
                  title="Produkte"
                  icon={<Package className="w-4 h-4" />}
                  count={results.products.length}
                >
                  {results.products.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => go(`/produkt/${p.slug}`)}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-stone-50 text-left transition-colors"
                      data-testid={`search-result-product-${p.slug}`}
                    >
                      {p.image ? (
                        <img src={p.image} alt="" className="w-12 h-12 rounded-md object-cover bg-stone-100 flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-stone-100 flex items-center justify-center text-stone-300 flex-shrink-0">
                          <Package className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-stone-800 truncate">{p.name}</div>
                        {p.description && <div className="text-xs text-stone-500 truncate">{p.description}</div>}
                      </div>
                      {p.base_price != null && (
                        <div className="text-sm font-semibold text-[#8a6d10] flex-shrink-0">
                          {(Number(p.base_price) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                        </div>
                      )}
                    </button>
                  ))}
                </SearchGroup>
              )}

              {/* Kategorien */}
              {results.categories?.length > 0 && (
                <SearchGroup
                  title="Kategorien"
                  icon={<LayoutGrid className="w-4 h-4" />}
                  count={results.categories.length}
                >
                  {results.categories.map((c) => (
                    <button
                      type="button"
                      key={c.id || c.slug}
                      onClick={() => go(`/kategorie/${c.slug}`)}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-stone-50 text-left transition-colors"
                      data-testid={`search-result-category-${c.slug}`}
                    >
                      {c.hero_image ? (
                        <img src={c.hero_image} alt="" className="w-12 h-12 rounded-md object-cover bg-stone-100 flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-amber-50 flex items-center justify-center text-[#8a6d10] flex-shrink-0">
                          <LayoutGrid className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-stone-800 truncate">{c.name}</div>
                        {c.description && <div className="text-xs text-stone-500 truncate">{c.description}</div>}
                      </div>
                    </button>
                  ))}
                </SearchGroup>
              )}

              {/* Angebote / Custom Pages */}
              {results.pages?.length > 0 && (
                <SearchGroup
                  title="Angebote & Seiten"
                  icon={<FileText className="w-4 h-4" />}
                  count={results.pages.length}
                >
                  {results.pages.map((pg) => (
                    <button
                      type="button"
                      key={pg.id || pg.slug}
                      onClick={() => go(`/seite/${pg.slug}`)}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-stone-50 text-left transition-colors"
                      data-testid={`search-result-page-${pg.slug}`}
                    >
                      {pg.hero_image ? (
                        <img src={pg.hero_image} alt="" className="w-12 h-12 rounded-md object-cover bg-stone-100 flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-stone-50 flex items-center justify-center text-stone-400 flex-shrink-0">
                          <FileText className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-stone-800 truncate">{pg.name}</div>
                        {pg.description && <div className="text-xs text-stone-500 truncate">{pg.description}</div>}
                      </div>
                    </button>
                  ))}
                </SearchGroup>
              )}
            </div>
          )}
        </div>

        {/* Fußzeile — Tastatur-Hinweise */}
        <div className="px-5 py-2.5 bg-stone-50 border-t border-stone-200 text-[11px] text-stone-500 flex items-center justify-between">
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-white border border-stone-200 font-mono">Esc</kbd> schließen
          </span>
          <span>Suche über Produkte, Kategorien &amp; Angebote</span>
        </div>
      </div>
    </div>
  );
}

function SearchGroup({ title, icon, count, children }) {
  return (
    <div>
      <div className="px-5 pt-3 pb-1.5 text-[11px] font-semibold text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
        <span className="text-stone-400">{icon}</span>
        <span>{title}</span>
        <span className="text-stone-300">({count})</span>
      </div>
      <div>{children}</div>
    </div>
  );
}
